import * as React from 'react';

import {RouteComponentProps, withRouter} from 'react-router';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, SaveUserRequest, User} from "../api/dtos/dtos";
import {Button, Icon, Modal, Table} from "antd";
import locales from "../locales";
import UserForm from "./UserForm";
import ButtonBar from "../common/ButtonBar";
import accountProvider, {Role} from "../account/AccountProvider";
import {ColumnProps} from "antd/lib/table";
import {indexRowKeyFn, simpleColumn} from "../utils/table-utils";
import ButtonGroup from "antd/lib/button/button-group";
import furoApi from "../api/FuroApi";
import {userRoleToText} from "./dto";
import * as paths from "../paths";
import {showSaveSuccessNotification} from "../utils/dialogs";

interface State {
  error?: ErrorResponse,
  saveError?: ErrorResponse,
  users: User[];
  editedUser?: User;
  modalTitle?: string;
  passwordRequired?: boolean;
  onModalSave?: (userRequest: SaveUserRequest) => void;
  disabledUsersOpen: boolean;
}

const compareByUserName = (a: User, b: User) =>
  (a.userName ?? '').localeCompare(b.userName ?? '', 'hu');



class UserManagementPage extends React.Component<RouteComponentProps<{}, {}, {}>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;


  constructor(props: RouteComponentProps<{}, {}, {}>) {
    super(props);
    this.state = {
      users: [],
      disabledUsersOpen: false,
    }
  }

  public componentDidMount() {
    this.loadUsers();
  }

  public render() {
    const columns: Array<ColumnProps<User>> = [
      simpleColumn(locales.username, r => this.renderUserName(r)),
      simpleColumn(locales.user_role, r => this.renderRole(r)),
      {key: 'actions', width: 48, render: this.renderActionColumn},
    ];

    const {error, saveError, editedUser, onModalSave, passwordRequired, modalTitle, users, disabledUsersOpen} = this.state;
    const sortedUsers = [...users].sort(compareByUserName);
    const enabledUsers = sortedUsers.filter(u => u.enabled);
    const disabledUsers = sortedUsers.filter(u => !u.enabled);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus"
                  onClick={this.onNewUser}>{locales.user_add}</Button>
        </ButtonBar>

        <Table
          style={{marginBottom: 24}}
          columns={columns}
          dataSource={enabledUsers}
          rowKey={indexRowKeyFn}
          bordered={true}
          pagination={false}
          size="small"
          scroll={{x: 442}}
        />

        {disabledUsers.length > 0 && <>
          <h3 style={{cursor: 'pointer', userSelect: 'none'}} onClick={this.toggleDisabledUsers}>
            <Icon type={disabledUsersOpen ? 'caret-down' : 'caret-right'} style={{marginRight: 8}}/>
            {locales.user_disabled_users}
          </h3>
          {disabledUsersOpen && <Table
            style={{marginBottom: 24}}
            columns={columns}
            dataSource={disabledUsers}
            rowKey={indexRowKeyFn}
            bordered={true}
            pagination={false}
            size="small"
            scroll={{x: 442}}
          />}
        </>}


        <Modal
          title={modalTitle}
          visible={editedUser !== undefined}
          footer={null}
          destroyOnClose={true}
          onCancel={this.onModalCancel}
        >
          {editedUser && <UserForm
              user={editedUser}
              passwordMandatory={passwordRequired ?? false}
              onFormSubmit={onModalSave}
              onFormCancel={this.onModalCancel}
          />}
          {saveError && <ErrorAlert style={{marginTop: '16px'}} error={saveError}/>}
        </Modal>


        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onNewUser = () => {
    this.setState({
      editedUser: {
        userName: undefined,
        enabled: true,
        role: Role.ADMIN,
      },
      passwordRequired: true,
      onModalSave: this.onUserCreate,
      modalTitle: locales.user_create
    });
  };

  private toggleDisabledUsers = () => {
    this.setState(prevState => ({disabledUsersOpen: !prevState.disabledUsersOpen}));
  };

  private renderUserName =(record: User) => {
    return (
      <div style={!record.enabled ? {color: 'lightgray'} : undefined}>
        {record.userName}
      </div>
    );
  };

  private renderRole = (record: User) => {
    return (
      <div style={!record.enabled ? {color: 'lightgray'} : undefined}>
        {userRoleToText[record.role]}
      </div>
    );
  };

  private renderActionColumn = (_: any, record: User, index: number) => {
    const onEditRentalItemClick = () => this.onEditUserClick(record, index);
    return <div className="action-column" style={{textAlign: 'right'}}>
      <ButtonGroup>
        <Button icon="edit"
                onClick={onEditRentalItemClick}
                title={locales.edit}/>
      </ButtonGroup>
    </div>;
  };

  private onEditUserClick = (user: User, _: number) => {
    this.setState({
      editedUser: user,
      onModalSave: this.onUserEdit,
      passwordRequired: false,
      modalTitle: locales.user_edit,
    });
  };

  private onModalCancel = () => {
    this.setState({editedUser: undefined, saveError: undefined});
  };

  private onUserEdit = (userRequest: SaveUserRequest) => {
    const promise = furoApi.users.update(userRequest)
      .then(this.onEditedUser)
      .catch(this.onSaveApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onEditedUser = () => {
    const {editedUser} = this.state;
    showSaveSuccessNotification();
    // if the current user were edited, logout needed
    if (editedUser?.userName === accountProvider.getUserName()) {
      this.logoutAction();
    } else {
      this.loadUsers();
    }
  };

  private logoutAction = () => {
    setTimeout(() => {
      accountProvider.clearAccountStorage();
      this.props.history.replace(paths.loginPath);
    });
  };

  private onUserCreate = (userRequest: SaveUserRequest) => {
    const promise = furoApi.users.create(userRequest)
      .then(this.loadUsers)
      .catch(this.onSaveApiError)
    withLoadingOverlay(promise, this.loadingHandler);
    showSaveSuccessNotification();
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then(this.onLoadUsersSuccess)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadUsersSuccess = (response: User[]) => {
    this.setState({
      users: response,
      editedUser: undefined,
      error: undefined,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onSaveApiError = (error: ErrorResponse) => {
    this.setState({saveError: error});
  };
}

export default withRouter(UserManagementPage);



// WEBPACK FOOTER //
// ./src/user-management/UserManagementPage.tsx